import {Color} from '@benchling/nucleus-ui';
import {Loader, Popover, ScrollArea, TextInput, Tooltip, UnstyledButton, createStyles} from '@mantine/core';
import type {MantineTheme} from '@mantine/core';
import {useDebouncedValue} from '@mantine/hooks';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {fetchProjectFolderTypeahead, fetchUserTypeahead, type UserTypeaheadItem} from 'omnibox/OmniboxDAO';
import {buildFolderFilterItems, type OmniboxFilter} from 'omnibox/useTypeaheadData';
import BenchlingIcon, {type BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import * as logging from 'util/Logging';

// The three type-toggle pills are mutually exclusive; "user" (Authored By) and "project"
// (Project or Folder) each open a dropdown.
export type OmniboxFilterTabId = 'entry' | 'entity' | 'file' | 'user' | 'project';

const TYPE_TABS: ReadonlyArray<{id: 'entry' | 'entity' | 'file'; label: string; icon: BenchlingIconName}> = [
  {id: 'entry', label: 'Entry', icon: 'file-lines-outline'},
  {id: 'entity', label: 'Entity', icon: 'square-list-outline'},
  {id: 'file', label: 'File', icon: 'paperclip'},
];

const useStyles = createStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    boxSizing: 'border-box' as const,
    // Fixed-height row inside the omnibox's flex column: never shrink when results fill the modal to
    // its max-height, or the pills row gets squeezed upward as results land.
    flexShrink: 0,
    height: 30,
    padding: '0px 16px 8px',
    gap: 8,
    backgroundColor: theme.white,
    // Side borders only, per Figma (Frame 1000002826).
    borderLeft: `1px solid ${Color.GRAY_400}`,
    borderRight: `1px solid ${Color.GRAY_400}`,
  },
  // Group 38: the pill group, left-aligned within the row.
  pillGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    height: 22,
  },
  iconWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 18,
    height: 18,
    borderRadius: 27,
    backgroundColor: Color.GRAY_100,
    color: Color.GRAY_600,
  },
  dropdown: {
    padding: 0,
  },
  // Sized to the widest author row so names and handles show in full, with a floor that keeps the
  // loading and empty states from collapsing and a cap past which long rows truncate.
  authorDropdown: {
    minWidth: 260,
    maxWidth: 400,
  },
  folderDropdown: {
    padding: 0,
  },
  searchWrapper: {
    padding: 8,
    borderBottom: `1px solid ${Color.GRAY_150}`,
  },
  // ScrollArea.Autosize wraps its content in a flex column that would otherwise grow to fit the
  // widest row, pushing long names, handles, and the scrollbar past the dropdown's max width.
  autosizeScrollArea: {
    '& > div': {
      minWidth: 0,
    },
  },
  // The scroll viewport lays its content out as a table, which also sizes to the widest row; a
  // block fills the viewport width so long rows truncate instead.
  scrollViewport: {
    '& > div': {
      display: 'block !important',
    },
  },
  optionList: {
    padding: 4,
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    padding: '6px 8px',
    borderRadius: theme.radius.sm,
    fontSize: 13,
    '&:hover': {
      backgroundColor: theme.colors.gray[0],
    },
  },
  // A tree row pairs an expand/collapse toggle with the selectable option; indentation is applied
  // per-row by nesting depth.
  treeRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  treeSelect: {
    flex: 1,
    minWidth: 0,
    width: 'auto',
  },
  expandToggle: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: theme.radius.sm,
    color: Color.GRAY_600,
    '&:hover': {
      backgroundColor: theme.colors.gray[1],
    },
  },
  // Aligns rows with no children with those that have an expand toggle.
  expandSpacer: {
    flexShrink: 0,
    width: 22,
  },
  // Spinner shown in place of a folder's children while they are being fetched on expand.
  childLoadingRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 8px',
  },
  treeChevron: {
    fontSize: 12,
    color: Color.GRAY_600,
  },
  optionName: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  optionHandle: {
    color: Color.GRAY_600,
    fontSize: 11,
    maxWidth: '50%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  optionCheck: {
    flexShrink: 0,
    color: theme.colors.blue[6],
    width: 14,
    fontSize: 12,
  },
  // Icon inside a pill trigger (type/author/project) and its dropdown chevron.
  pillIcon: {
    fontSize: 12,
  },
  pillChevron: {
    fontSize: 8,
  },
  // Leading icon on a project/folder option row.
  optionIcon: {
    flexShrink: 0,
    fontSize: 12,
    color: Color.GRAY_600,
  },
  emptyOption: {
    padding: '8px 12px',
    fontSize: 12,
    color: Color.GRAY_600,
    textAlign: 'center' as const,
  },
  loadingOption: {
    display: 'flex',
    justifyContent: 'center',
    padding: '8px 12px',
  },
  // Fill and center within the fixed-height dropdown so it holds a steady shape across states.
  loadingState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 224,
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 224,
    padding: '8px 12px',
    fontSize: 12,
    color: Color.GRAY_600,
    textAlign: 'center' as const,
  },
}));

// Shared visual treatment for every filter pill (type toggles + the author trigger).
function pillSx(isActive: boolean) {
  return (theme: MantineTheme) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    width: 'fit-content',
    height: 22,
    whiteSpace: 'nowrap' as const,
    borderRadius: 10,
    border: `1px solid ${Color.GRAY_300}`,
    padding: '2px 9px 2px 2px',
    // Active pills use the selected label treatment from Figma (600 / 12px, blue[7] = BLUE_450);
    // inactive pills stay 400 / 10px in GRAY_900.
    fontSize: isActive ? 12 : 10,
    fontWeight: isActive ? 600 : 400,
    lineHeight: '16px',
    letterSpacing: 0,
    color: isActive ? theme.colors.blue[7] : Color.GRAY_900,
    backgroundColor: isActive ? theme.colors.blue[0] : 'transparent',
    transition: 'all 100ms ease',
    '&:hover': {
      backgroundColor: isActive ? theme.colors.blue[0] : theme.colors.gray[0],
    },
  });
}

/** Option label that shows the full name in a tooltip on hover, since long names are cut off. */
function OptionName({name}: {name: string}): JSX.Element {
  const {classes} = useStyles();
  return (
    // The omnibox Modal is zIndex 1055 and the filter dropdown 1056; the tooltip must sit above both.
    <Tooltip label={name} withinPortal={true} zIndex={1057} openDelay={300}>
      <span className={classes.optionName}>{name}</span>
    </Tooltip>
  );
}

interface AuthoredByPillProps {
  isActive: boolean;
  selectedAuthors: ReadonlyArray<OmniboxFilter>;
  onToggleAuthor: (author: OmniboxFilter) => void;
}

function AuthoredByPill({isActive, selectedAuthors, onToggleAuthor}: AuthoredByPillProps): JSX.Element {
  const {classes, cx} = useStyles();
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);

  const selectedAuthorIds = useMemo(() => new Set(selectedAuthors.map((a) => a.id)), [selectedAuthors]);

  // Author filtering is single-select, so the pill reflects the one chosen author inline instead of
  // rendering a separate filter chip. Falls back to the plain "Authored By" label when none is set.
  const pillLabel =
    selectedAuthors.length === 1
      ? `Authored by: ${selectedAuthors[0].isSelf ? 'You' : selectedAuthors[0].name}`
      : 'Authored By';

  // populate options when a user starts typing in the search input, debounced to avoid excessive API calls
  const [search, setSearch] = useState('');
  const [debouncedSearchValue] = useDebouncedValue(search, 300);
  const [options, setOptions] = useState<Array<UserTypeaheadItem>>([]);

  useEffect(() => {
    if (!opened) {
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    fetchUserTypeahead(debouncedSearchValue.trim())
      .then((users: Array<UserTypeaheadItem>) => {
        if (!cancelled) {
          setOptions(users);
        }
      })
      .catch((e) => {
        // Drop stale results on failure so a prior query's users can't be shown (or selected)
        // under the current search text.
        if (!cancelled) {
          setOptions([]);
        }
        // eslint-disable-next-line no-console
        console.error('Failed to fetch users in typeahead', e);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearchValue, opened]);

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      withinPortal={true}
      zIndex={1056} // The omnibox Modal renders at z-Index 1055; the portaled dropdown must sit above it (matching OmniboxFooter's Popover).
      shadow="md"
      radius="md"
    >
      <Popover.Target>
        <UnstyledButton
          aria-pressed={isActive}
          aria-haspopup="listbox"
          aria-expanded={opened}
          onClick={() => {
            setOpened((o) => !o);
          }}
          sx={pillSx(isActive)}
        >
          <span className={classes.iconWrapper}>
            <BenchlingIcon icon="image-polaroid-user" className={classes.pillIcon} aria-hidden="true" />
          </span>
          {pillLabel}
          <BenchlingIcon icon="chevron-down" className={classes.pillChevron} aria-hidden="true" />
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown className={cx(classes.dropdown, classes.authorDropdown)}>
        <div className={classes.searchWrapper}>
          <TextInput
            size="xs"
            placeholder="Filter by author..."
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
            }}
            autoFocus={true}
            aria-label="Filter by author"
          />
        </div>
        <ScrollArea.Autosize
          mah={240}
          type="auto"
          className={classes.autosizeScrollArea}
          classNames={{viewport: classes.scrollViewport}}
        >
          <div className={classes.optionList} role="listbox" aria-label="Authors">
            {loading ? (
              <div className={classes.loadingOption} role="status" aria-label="Loading authors">
                <Loader size="xs" />
              </div>
            ) : options.length === 0 ? (
              <div className={classes.emptyOption}>No matching people</div>
            ) : (
              options.map((author) => {
                const selected = selectedAuthorIds.has(author.apiId);
                return (
                  <UnstyledButton
                    key={author.apiId}
                    role="option"
                    aria-selected={selected}
                    className={classes.option}
                    onClick={() => {
                      onToggleAuthor({
                        kind: 'user',
                        id: author.apiId,
                        name: author.name,
                        handle: author.handle,
                        isSelf: author.isSelf,
                      });
                    }}
                  >
                    <span className={classes.optionName}>{author.isSelf ? 'You' : author.name}</span>
                    {author.handle && <span className={classes.optionHandle}>@{author.handle}</span>}
                    {selected && (
                      <BenchlingIcon icon="check" className={classes.optionCheck} aria-hidden="true" />
                    )}
                  </UnstyledButton>
                );
              })
            )}
          </div>
        </ScrollArea.Autosize>
      </Popover.Dropdown>
    </Popover>
  );
}

interface ProjectFolderPillProps {
  isActive: boolean;
  selectedProjectFolders: ReadonlyArray<OmniboxFilter>;
  onToggleProjectFolder: (item: OmniboxFilter) => void;
}

function ProjectFolderPill({
  isActive,
  selectedProjectFolders,
  onToggleProjectFolder,
}: ProjectFolderPillProps): JSX.Element {
  const {classes, cx} = useStyles();
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);

  const selectedProjectFolderIds = useMemo(
    () => new Set(selectedProjectFolders.map((f) => f.id)),
    [selectedProjectFolders]
  );

  const selectedProjectFolder = selectedProjectFolders.length === 1 ? selectedProjectFolders[0] : null;
  const pillLabel = selectedProjectFolder
    ? `${selectedProjectFolder.kind === 'folder' ? 'In folder' : 'In project'}: ${selectedProjectFolder.name}`
    : 'Project or Folder';

  // Query the server live as the user types, debounced to avoid a request per keystroke.
  const [search, setSearch] = useState('');
  const [debouncedSearchValue] = useDebouncedValue(search, 300);
  // With no search text the dropdown is a lazy tree: projects and their top-level folders load up
  // front, and deeper subfolders are fetched per-parent on expand. A search flattens the results.
  const treeMode = debouncedSearchValue.trim() === '';

  // Top level of the tree (projects + their top-level folders); the flat list for an active search.
  const [rootItems, setRootItems] = useState<Array<OmniboxFilter>>([]);
  const [searchOptions, setSearchOptions] = useState<Array<OmniboxFilter>>([]);
  // Memoized subfolder fetches keyed by the expanded folder's apiId, so re-expanding never refetches.
  const [childrenByParent, setChildrenByParent] = useState<Map<string, Array<OmniboxFilter>>>(new Map());
  const [loadingParents, setLoadingParents] = useState<ReadonlySet<string>>(new Set());
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(new Set());
  // The tree's top level is fetched once and kept for the component's lifetime; a search is fetched
  // per distinct query. Both persist across close/reopen, so reopening never re-hits the server.
  const rootsLoadedRef = useRef(false);
  const lastSearchRef = useRef<string | null>(null);

  useEffect(() => {
    if (!opened) {
      return undefined;
    }
    const trimmed = debouncedSearchValue.trim();
    const isRoots = trimmed === '';
    if (isRoots ? rootsLoadedRef.current : lastSearchRef.current === trimmed) {
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    fetchProjectFolderTypeahead(trimmed || undefined)
      .then((items) => {
        if (cancelled) {
          return;
        }
        const built = buildFolderFilterItems(items);
        if (isRoots) {
          rootsLoadedRef.current = true;
          setRootItems(built);
          // Projects start expanded so their top-level folders are visible; folders start collapsed
          // until expanded.
          setExpandedIds(new Set(built.filter((item) => item.kind === 'project').map((item) => item.id)));
        } else {
          lastSearchRef.current = trimmed;
          setSearchOptions(built);
        }
      })
      .catch((e) => {
        // Drop stale results on failure so a prior query's items can't be shown (or selected)
        // under the current search text.
        if (!cancelled) {
          if (isRoots) {
            setRootItems([]);
          } else {
            setSearchOptions([]);
          }
        }
        logging.error('Failed to fetch project/folder typeahead', {extra: {error: e}});
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearchValue, opened]);

  const fetchChildren = (folderApiId: string): void => {
    if (childrenByParent.has(folderApiId) || loadingParents.has(folderApiId)) {
      return;
    }
    setLoadingParents((prev) => new Set(prev).add(folderApiId));
    fetchProjectFolderTypeahead(undefined, folderApiId)
      .then((items) => {
        setChildrenByParent((prev) => new Map(prev).set(folderApiId, buildFolderFilterItems(items)));
      })
      .catch((e) => {
        // Cache an empty result so a failed expand resolves instead of spinning forever.
        setChildrenByParent((prev) => (prev.has(folderApiId) ? prev : new Map(prev).set(folderApiId, [])));
        logging.error('Failed to fetch subfolders', {extra: {error: e}});
      })
      .finally(() => {
        setLoadingParents((prev) => {
          const next = new Set(prev);
          next.delete(folderApiId);
          return next;
        });
      });
  };

  const toggleExpand = (item: OmniboxFilter): void => {
    const willExpand = !expandedIds.has(item.id);
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(item.id)) {
        next.delete(item.id);
      } else {
        next.add(item.id);
      }
      return next;
    });
    // Projects ship their top-level folders in the initial load; only folders fetch children lazily.
    if (willExpand && item.kind === 'folder') {
      fetchChildren(item.id);
    }
  };

  type TreeRow =
    | {type: 'item'; item: OmniboxFilter; depth: number; hasChildren: boolean}
    | {type: 'loading'; key: string; depth: number};

  const treeRows = useMemo((): Array<TreeRow> => {
    // Top-level folders arrive with the projects and are nested under their project's apiId.
    const topLevelByProject = new Map<string, Array<OmniboxFilter>>();
    for (const item of rootItems) {
      if (item.kind === 'folder' && item.parentApiId) {
        const siblings = topLevelByProject.get(item.parentApiId) ?? [];
        siblings.push(item);
        topLevelByProject.set(item.parentApiId, siblings);
      }
    }
    const rows: Array<TreeRow> = [];
    const walk = (nodes: Array<OmniboxFilter>, depth: number): void => {
      for (const node of nodes) {
        const isFolder = node.kind === 'folder';
        // Any folder may have subfolders; offer an expand toggle and fetch its level on demand. A
        // project is expandable only when it already carries top-level folders.
        const hasChildren = isFolder ? true : (topLevelByProject.get(node.id)?.length ?? 0) > 0;
        rows.push({type: 'item', item: node, depth, hasChildren});
        if (!hasChildren || !expandedIds.has(node.id)) {
          continue;
        }
        if (!isFolder) {
          walk(topLevelByProject.get(node.id) ?? [], depth + 1);
          continue;
        }
        const fetched = childrenByParent.get(node.id);
        if (fetched === undefined) {
          rows.push({type: 'loading', key: `loading:${node.id}`, depth: depth + 1});
        } else {
          walk(fetched, depth + 1);
        }
      }
    };
    walk(
      rootItems.filter((item) => item.kind === 'project'),
      0
    );
    return rows;
  }, [rootItems, childrenByParent, expandedIds]);

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      // Anchor to the pill's right edge so the dropdown opens leftward. Left-aligning it would push
      // the menu rightward under the adjacent Authored By pill.
      position="bottom-end"
      // Fixed width; Mantine otherwise sizes the dropdown to its content, which changes between states.
      // Wide enough that nested folder names stay readable after per-level indentation.
      width={320}
      withinPortal={true}
      // The omnibox Modal renders at zIndex 1055; the portaled dropdown must sit above it
      // (matching OmniboxFooter's Popover) or it renders behind the modal and is invisible.
      zIndex={1056}
      shadow="md"
      radius="md"
    >
      <Popover.Target>
        <UnstyledButton
          aria-pressed={isActive}
          aria-haspopup="listbox"
          aria-expanded={opened}
          onClick={() => {
            setOpened((o) => !o);
          }}
          sx={pillSx(isActive)}
        >
          <span className={classes.iconWrapper}>
            <BenchlingIcon icon="folder-open" className={classes.pillIcon} aria-hidden="true" />
          </span>
          {pillLabel}
          <BenchlingIcon icon="chevron-down" className={classes.pillChevron} aria-hidden="true" />
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown className={classes.folderDropdown}>
        <div className={classes.searchWrapper}>
          <TextInput
            size="xs"
            placeholder="Filter by project or folder..."
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
            }}
            autoFocus={true}
            aria-label="Filter by project or folder"
          />
        </div>
        {/* Fixed height so the dropdown holds a steady shape across states; scrolls on overflow. */}
        <ScrollArea h={240} type="auto">
          <div className={classes.optionList} role="listbox" aria-label="Projects and folders">
            {loading ? (
              <div className={classes.loadingState} role="status" aria-label="Loading projects and folders">
                <Loader size="xs" aria-hidden="true" />
              </div>
            ) : treeMode ? (
              treeRows.length === 0 ? (
                <div className={classes.emptyState}>No matching projects or folders</div>
              ) : (
                treeRows.map((row) => {
                  if (row.type === 'loading') {
                    return (
                      <div
                        key={row.key}
                        className={classes.childLoadingRow}
                        style={{paddingLeft: row.depth * 16}}
                        role="status"
                        aria-label="Loading subfolders"
                      >
                        <Loader size="xs" aria-hidden="true" />
                      </div>
                    );
                  }
                  const {item, depth, hasChildren} = row;
                  const selected = selectedProjectFolderIds.has(item.id);
                  const isFolder = item.kind === 'folder';
                  const expanded = expandedIds.has(item.id);
                  return (
                    <div
                      key={`${item.kind}:${item.id}`}
                      className={classes.treeRow}
                      style={{paddingLeft: depth * 16}}
                    >
                      {hasChildren ? (
                        <UnstyledButton
                          className={classes.expandToggle}
                          aria-label={expanded ? 'Collapse' : 'Expand'}
                          aria-expanded={expanded}
                          onClick={() => {
                            toggleExpand(item);
                          }}
                        >
                          <BenchlingIcon
                            icon={expanded ? 'chevron-down' : 'chevron-right'}
                            className={classes.treeChevron}
                            aria-hidden="true"
                          />
                        </UnstyledButton>
                      ) : (
                        <span className={classes.expandSpacer} />
                      )}
                      <UnstyledButton
                        role="option"
                        aria-selected={selected}
                        className={cx(classes.option, classes.treeSelect)}
                        onClick={() => {
                          onToggleProjectFolder(item);
                        }}
                      >
                        <BenchlingIcon
                          icon={isFolder ? 'folder-open' : 'square-list-outline'}
                          className={classes.optionIcon}
                          aria-hidden="true"
                        />
                        <OptionName name={item.name} />
                        {selected && (
                          <BenchlingIcon icon="check" className={classes.optionCheck} aria-hidden="true" />
                        )}
                      </UnstyledButton>
                    </div>
                  );
                })
              )
            ) : searchOptions.length === 0 ? (
              <div className={classes.emptyState}>No matching projects or folders</div>
            ) : (
              searchOptions.map((item) => {
                const selected = selectedProjectFolderIds.has(item.id);
                const isFolder = item.kind === 'folder';
                return (
                  <UnstyledButton
                    key={`${item.kind}:${item.id}`}
                    role="option"
                    aria-selected={selected}
                    className={classes.option}
                    onClick={() => {
                      onToggleProjectFolder(item);
                    }}
                  >
                    <BenchlingIcon
                      icon={isFolder ? 'folder-open' : 'square-list-outline'}
                      className={classes.optionIcon}
                      aria-hidden="true"
                    />
                    <OptionName name={item.name} />
                    {item.projectName && <span className={classes.optionHandle}>{item.projectName}</span>}
                    {selected && (
                      <BenchlingIcon icon="check" className={classes.optionCheck} aria-hidden="true" />
                    )}
                  </UnstyledButton>
                );
              })
            )}
          </div>
        </ScrollArea>
      </Popover.Dropdown>
    </Popover>
  );
}

interface OmniboxFilterTabsProps {
  activeTabIds: ReadonlySet<OmniboxFilterTabId>;
  onTabClick: (id: 'entry' | 'entity' | 'file') => void;
  selectedAuthors: ReadonlyArray<OmniboxFilter>;
  onToggleAuthor: (author: OmniboxFilter) => void;
  selectedProjectFolders: ReadonlyArray<OmniboxFilter>;
  onToggleProjectFolder: (item: OmniboxFilter) => void;
  className?: string;
}

export default function OmniboxFilterTabs({
  activeTabIds,
  onTabClick,
  selectedAuthors,
  onToggleAuthor,
  selectedProjectFolders,
  onToggleProjectFolder,
  className,
}: OmniboxFilterTabsProps): JSX.Element {
  const {classes} = useStyles();
  return (
    <div className={className ?? classes.root}>
      <div role="group" aria-label="Filter search results by type" className={classes.pillGroup}>
        {TYPE_TABS.map((tab) => {
          const isActive = activeTabIds.has(tab.id);
          return (
            <UnstyledButton
              key={tab.id}
              aria-pressed={isActive}
              onClick={() => {
                onTabClick(tab.id);
              }}
              sx={pillSx(isActive)}
            >
              <span className={classes.iconWrapper}>
                <BenchlingIcon icon={tab.icon} className={classes.pillIcon} aria-hidden="true" />
              </span>
              {tab.label}
            </UnstyledButton>
          );
        })}
        <ProjectFolderPill
          isActive={activeTabIds.has('project')}
          selectedProjectFolders={selectedProjectFolders}
          onToggleProjectFolder={onToggleProjectFolder}
        />
        <AuthoredByPill
          isActive={activeTabIds.has('user')}
          selectedAuthors={selectedAuthors}
          onToggleAuthor={onToggleAuthor}
        />
      </div>
    </div>
  );
}
